import apiClient from '@/lib/api-client';
import { ApiError } from '@/model/ApiError';
import { Module, Mutation, VuexModule, Action } from 'vuex-module-decorators';
import { ProfileResponse } from '@/model/Profile';
import {
    createEventRequestFromFormData,
    EventFormData,
    EventRequest,
    EventResponse,
    EventFlowSettingsUpdate,
    EventUpdate,
    InviteUserRequest,
    RemoveUserRequest,
} from '@/model/Event';
import { createDateFromUTCString } from '@/lib/date-util';
import router from '@/router';
import store from '..';

@Module({ namespaced: true, preserveState: false })
export default class EventStorage extends VuexModule {
    error: ApiError | null = null;

    events: EventResponse[] = new Array<EventResponse>();

    event: EventResponse | null = null;

    invitableUsers: ProfileResponse[] = new Array<ProfileResponse>();

    invitedUsers: ProfileResponse[] = new Array<ProfileResponse>();

    @Mutation
    setError(error: ApiError | null): void {
        this.error = error;
    }

    @Mutation
    setEvents(events: EventResponse[]): void {
        events?.forEach((event) => {
            if (event?.startDate) {
                event.startDate = createDateFromUTCString(
                    event.startDate?.toString(),
                );
            }
        });

        this.events = events;
    }

    @Mutation
    deleteEventFromEvents(eventId: number): void {
        if (this.events) {
            this.events = this.events.filter((event) => event.id !== eventId);
        }
    }

    @Mutation
    setEvent(event: EventResponse | null): void {
        if (event) {
            event.selectedStartDate = createDateFromUTCString(
                event.selectedStartDate.toString(),
            );
            event.selectedEndDate = createDateFromUTCString(
                event.selectedEndDate.toString(),
            );
            event.startDate = new Date(event.startDate);
        }
        this.event = event;
    }

    @Mutation
    setInvitableUsers(invitableUsers: ProfileResponse[]): void {
        this.invitableUsers = invitableUsers;
    }

    @Mutation
    setInvitedUsers(invitedUsers: ProfileResponse[]): void {
        this.invitedUsers = invitedUsers;
    }

    @Action
    fetchEvents(): Promise<void | EventResponse[]> {
        return apiClient
            .get<void, EventResponse[]>('/events')
            .then((data: EventResponse[]) => {
                this.context.commit('setEvents', data);
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    create(formData: EventFormData): Promise<void | EventResponse> {
        return apiClient
            .post<EventRequest, EventResponse>(
                `/projects/${formData.projectId}/events`,
                createEventRequestFromFormData(formData),
            )
            .then((data: EventResponse) => {
                this.context.commit('setError', null);
                return Promise.resolve(data);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    fetchEventById(id: number): Promise<void | EventResponse> {
        return apiClient
            .get<void, EventResponse>(`/events/${id}`)
            .then((data: EventResponse) => {
                this.context.commit('setEvent', data);
                this.context.commit('setError', null);
            })
            .catch((error) => {
                if (error?.key === 'security.permission.denied') {
                    router.push({ name: 'NotFound' });
                }

                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    fetchInvitableUsers(id: number): Promise<void | ProfileResponse[]> {
        return apiClient
            .get<void, ProfileResponse[]>(`/events/${id}/invitable`)
            .then((data: ProfileResponse[]) => {
                this.context.commit('setInvitableUsers', data);
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    fetchInvitedUsers(id: number): Promise<void | ProfileResponse[]> {
        return apiClient
            .get<void, ProfileResponse[]>(`/events/${id}/users`)
            .then((data: ProfileResponse[]) => {
                this.context.commit('setInvitedUsers', data);
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    inviteUser(inviteUserRequest: InviteUserRequest): Promise<void> {
        return apiClient
            .post<void>(`/events/${inviteUserRequest.eventId}/invite`, {
                data: inviteUserRequest.userId,
            })
            .then(() => {
                this.context.commit('setError', null);
                store.commit(
                    'customerProjectStorage/addProjectUserEventId',
                    inviteUserRequest,
                );
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    removeUser(removeUserRequest: RemoveUserRequest): Promise<void> {
        return apiClient
            .delete<void>(
                `/events/${removeUserRequest.eventId}/${removeUserRequest.userId}`,
            )
            .then(() => {
                this.context.commit('setError', null);
                store.commit(
                    'customerProjectStorage/removeProjectUserEventId',
                    removeUserRequest,
                );
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    deleteEvent(eventId: number): Promise<void> {
        return apiClient
            .delete<void>(`/events/${eventId}`)
            .then(() => {
                this.context.commit('setError', null);
                this.context.commit('deleteEventFromEvents', eventId);
                return Promise.resolve();
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    updateEvent(eventUpdate: EventUpdate): Promise<void> {
        return apiClient
            .put(`/events/${eventUpdate.eventId}`, eventUpdate.eventRequest)
            .then(() => {
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    updateEventFlowSettings(request: EventFlowSettingsUpdate): Promise<void> {
        return apiClient
            .put(
                `/events/${request.eventId}/event-settings`,
                request.selectedDate,
            )
            .then(() => {
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    get getError(): ApiError | null {
        return this.error;
    }

    get getEvents(): Array<EventResponse> | null {
        return this.events;
    }

    get getEvent(): EventResponse | null {
        return this.event;
    }

    get getInvitableUsers(): Array<ProfileResponse> {
        return this.invitableUsers;
    }

    get getInvitedUsers(): Array<ProfileResponse> {
        return this.invitedUsers;
    }
}
